import dayjs from "dayjs";
import PropTypes from "prop-types";

import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import { formatDate } from "@/features/shared";

// 9.5" x 11" continuous form. The manufacturer's 9.5" "printable" width
// excludes tractor-hole strips, but real printers reserve ~0.5" of
// unprintable margin on each side for the print head's travel range. Padding
// here keeps content inside that practical print area (~8.5" wide).
const PAGE_SIZE = [684, 792];

const styles = StyleSheet.create({
  page: {
    paddingHorizontal: 36,
    paddingVertical: 26,
    fontSize: 11,
    fontFamily: "Helvetica",
    color: "#000",
    lineHeight: 1.2,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  logo: {
    width: 55,
    height: "auto",
  },
  titleColumn: {
    alignItems: "flex-end",
    flexShrink: 1,
  },
  title: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
  },
  titleRef: {
    fontSize: 13,
    marginTop: 12,
  },
  infoCard: {
    borderWidth: 0.5,
    borderColor: "#999",
  },
  infoBand: {
    padding: 8,
    borderTopWidth: 0.5,
    borderColor: "#999",
  },
  infoBandRow: {
    flexDirection: "row",
  },
  infoBandRowDivided: {
    flexDirection: "row",
    borderTopWidth: 0.5,
    borderColor: "#999",
  },
  infoBandCell: {
    padding: 8,
  },
  infoBandCellDivided: {
    padding: 8,
    borderRightWidth: 0.5,
    borderColor: "#999",
  },
  partyLabel: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    marginBottom: 4,
  },
  partyName: {
    fontFamily: "Helvetica-Bold",
  },
  warehouseLine: {
    marginTop: 4,
  },
  metaLabel: {
    fontFamily: "Helvetica-Bold",
  },
  tableContainer: {
    marginTop: 8,
    borderWidth: 0.5,
    borderColor: "#999",
  },
  tableHeader: {
    flexDirection: "row",
    fontFamily: "Helvetica-Bold",
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderColor: "#999",
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  tableFooter: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderTopWidth: 0.5,
    borderColor: "#999",
    fontFamily: "Helvetica-Bold",
  },
  cell: {
    paddingHorizontal: 4,
    borderRightWidth: 0.5,
    borderColor: "#999",
  },
  rightCell: {
    textAlign: "right",
    paddingHorizontal: 4,
    borderRightWidth: 0.5,
    borderColor: "#999",
  },
  signatureSection: {
    flexDirection: "row",
    marginTop: 36,
  },
  signatureCell: {
    flex: 1,
    alignItems: "center",
  },
  signatureLabel: {
    fontFamily: "Helvetica-Bold",
  },
  signatureSubColumn: {
    width: "70%",
    marginTop: 56,
  },
  signatureLine: {
    borderBottomWidth: 0.5,
    borderColor: "#000",
  },
  signatureDate: {
    fontSize: 9,
    marginTop: 4,
  },
});

const COL = {
  index: 0.5,
  name: 6,
  qty: 1,
  note: 3,
};

const SIGNATURE_ROLES = ["Gudang", "Kurir", "Penerima"];

export const DeliveryOrderPdf = ({ deliveryOrder }) => {
  const sellerAddressLines = (deliveryOrder.seller?.address ?? "")
    .split("\n")
    .filter(Boolean);
  const buyerAddressLines = (deliveryOrder.buyer?.address ?? "")
    .split("\n")
    .filter(Boolean);

  const productVariants = [...deliveryOrder.deliveryOrderProductVariants].sort(
    (a, b) => a.name.localeCompare(b.name)
  );

  const quantityTotal = productVariants.reduce(
    (sum, variant) => sum + variant.quantity,
    0
  );

  return (
    <Document>
      <Page size={PAGE_SIZE} style={styles.page}>
        <View style={styles.headerRow}>
          <Image style={styles.logo} src="/logo.png" />
          <View style={styles.titleColumn}>
            <Text style={styles.title}>SURAT JALAN</Text>
            <Text style={styles.titleRef}>{deliveryOrder.referenceNumber}</Text>
          </View>
        </View>

        <View style={styles.infoCard}>
          <View style={styles.infoBandRow}>
            <View
              style={[
                styles.infoBandCellDivided,
                { flexGrow: 0, flexShrink: 0, flexBasis: "50%" },
              ]}
            >
              <Text style={styles.partyLabel}>PENJUAL :</Text>
              <Text style={styles.partyName}>{deliveryOrder.seller?.name}</Text>
              {sellerAddressLines.map((line, idx) => (
                <Text key={idx}>{line}</Text>
              ))}
              {deliveryOrder.warehouse?.name && (
                <Text style={styles.warehouseLine}>
                  Gudang: {deliveryOrder.warehouse.name}
                </Text>
              )}
            </View>
            <View
              style={[
                styles.infoBandCell,
                { flexGrow: 0, flexShrink: 0, flexBasis: "50%" },
              ]}
            >
              <Text style={styles.partyLabel}>PEMBELI :</Text>
              <Text style={styles.partyName}>{deliveryOrder.buyer?.name}</Text>
              {buyerAddressLines.map((line, idx) => (
                <Text key={idx}>{line}</Text>
              ))}
            </View>
          </View>
          <View style={styles.infoBandRowDivided}>
            <View
              style={[
                styles.infoBandCellDivided,
                { flexGrow: 0, flexShrink: 0, flexBasis: "25%" },
              ]}
            >
              <Text style={styles.metaLabel}>Tanggal Pengiriman</Text>
              <Text>{formatDate(dayjs(deliveryOrder.transactionDate))}</Text>
            </View>
            <View
              style={[
                styles.infoBandCellDivided,
                { flexGrow: 0, flexShrink: 0, flexBasis: "25%" },
              ]}
            >
              <Text style={styles.metaLabel}>No. Referensi Pembeli</Text>
              <Text>{deliveryOrder.buyerReferenceNumber ?? "-"}</Text>
            </View>
            <View
              style={[
                styles.infoBandCell,
                { flexGrow: 0, flexShrink: 0, flexBasis: "50%" },
              ]}
            >
              <Text style={styles.metaLabel}>Catatan</Text>
              <Text>{deliveryOrder.note ?? "-"}</Text>
            </View>
          </View>
        </View>

        <View style={styles.tableContainer}>
          <View style={styles.tableHeader}>
            <Text style={[styles.cell, { flex: COL.index }]}>#</Text>
            <Text style={[styles.cell, { flex: COL.name }]}>Nama Barang</Text>
            <Text style={[styles.rightCell, { flex: COL.qty }]}>Qty</Text>
            <Text
              style={[
                styles.cell,
                { flex: COL.note, borderRightWidth: 0 },
              ]}
            >
              Catatan
            </Text>
          </View>
          {productVariants.map((variant, index) => {
            const unit = variant.product?.unitOfMeasurement?.name;
            return (
              <View style={styles.tableRow} key={variant.id} wrap={false}>
                <Text style={[styles.cell, { flex: COL.index }]}>
                  {index + 1}
                </Text>
                <Text style={[styles.cell, { flex: COL.name }]}>
                  {variant.name}
                </Text>
                <Text style={[styles.rightCell, { flex: COL.qty }]}>
                  {variant.quantity}
                  {unit ? ` ${unit}` : ""}
                </Text>
                <Text
                  style={[
                    styles.cell,
                    { flex: COL.note, borderRightWidth: 0 },
                  ]}
                >
                  {variant.note || "-"}
                </Text>
              </View>
            );
          })}
          <View style={styles.tableFooter}>
            <Text style={[styles.cell, { flex: COL.index }]}>{" "}</Text>
            <Text style={[styles.cell, { flex: COL.name }]}>Total</Text>
            <Text style={[styles.rightCell, { flex: COL.qty }]}>
              {quantityTotal}
            </Text>
            <Text style={[styles.cell, { flex: COL.note, borderRightWidth: 0 }]}>
              {" "}
            </Text>
          </View>
        </View>

        <View style={styles.signatureSection} wrap={false}>
          {SIGNATURE_ROLES.map((label) => (
            <View key={label} style={styles.signatureCell}>
              <Text style={styles.signatureLabel}>{label}</Text>
              <View style={styles.signatureSubColumn}>
                <View style={styles.signatureLine} />
                <Text style={styles.signatureDate}>Tgl.</Text>
              </View>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

DeliveryOrderPdf.propTypes = {
  deliveryOrder: PropTypes.object.isRequired,
};
